import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Shield, RotateCcw, Save, Search } from "lucide-react";

const ROLE_BADGE = {
  super_manager: "bg-purple-100 text-purple-800 border-purple-300",
  manager:       "bg-emerald-100 text-emerald-800 border-emerald-300",
  cashier:       "bg-blue-100 text-blue-800 border-blue-300",
  waiter:        "bg-amber-100 text-amber-800 border-amber-300",
  driver:        "bg-slate-100 text-slate-700 border-slate-300",
};

const ROLE_LABEL = {
  super_manager: "Σούπερ Manager",
  manager: "Manager",
  cashier: "Ταμίας",
  waiter: "Σερβιτόρος",
  driver: "Οδηγός",
};

/** Matrix view of operators × permissions — single screen lets the owner
 * audit and adjust everyone in one place instead of opening 10 dialogs.
 *
 * Layout: first column = operator name (sticky), then one column per
 * permission key from `/permissions/catalog`. Each cell is a Switch
 * bound to the user's `permissions[key]` flag. Pending changes light
 * up the row with an amber border until the operator hits «Αποθήκευση».
 */
export default function PermissionsMatrixTab({ currentUser }) {
  const [catalog, setCatalog] = useState({ fields: [], roles: [] });
  const [users, setUsers] = useState([]);
  const [dirty, setDirty] = useState({}); // { userId: { permKey: bool } }
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [templates, setTemplates] = useState({}); // role → permissions

  const isSuper = currentUser?.role === "super_manager";

  const load = async () => {
    setBusy(true);
    try {
      const [cat, us, tpl] = await Promise.all([
        http.get("/permissions/catalog"),
        http.get("/users"),
        http.get("/permissions/templates"),
      ]);
      setCatalog(cat.data || { fields: [], roles: [] });
      // Hide super_manager rows for non-super-manager viewers; the role
      // itself stays editable only via the dedicated Operators tab.
      const list = (us.data || []).filter((u) => isSuper || u.role !== "super_manager");
      setUsers(list);
      const tplMap = {};
      (tpl.data || []).forEach((t) => { tplMap[t.role] = t.permissions; });
      setTemplates(tplMap);
      setDirty({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const togglePerm = (userId, permKey, current) => {
    setDirty((d) => {
      const next = { ...d, [userId]: { ...(d[userId] || {}), [permKey]: !current } };
      // If toggled back to original value, drop the dirty entry.
      const user = users.find((u) => u.id === userId);
      const original = !!(user?.permissions || {})[permKey];
      if (next[userId][permKey] === original) {
        delete next[userId][permKey];
        if (!Object.keys(next[userId]).length) delete next[userId];
      }
      return next;
    });
  };

  const resetRowToTemplate = (userId) => {
    const u = users.find((x) => x.id === userId);
    if (!u) return;
    const tpl = templates[u.role] || {};
    const changes = {};
    for (const f of catalog.fields) {
      const cur = !!(u.permissions || {})[f.key];
      const tplVal = !!tpl[f.key];
      if (cur !== tplVal) changes[f.key] = tplVal;
    }
    if (!Object.keys(changes).length) {
      toast.info("Ήδη ίδιο με το template");
      return;
    }
    setDirty((d) => ({ ...d, [userId]: { ...(d[userId] || {}), ...changes } }));
  };

  const saveOne = async (userId) => {
    const u = users.find((x) => x.id === userId);
    if (!u || !dirty[userId]) return;
    const merged = { ...(u.permissions || {}), ...dirty[userId] };
    try {
      await http.put(`/users/${userId}`, { permissions: merged });
      toast.success(`✓ ${u.name}`);
      // Update local users state with the merged permissions.
      setUsers((arr) => arr.map((x) => x.id === userId ? { ...x, permissions: merged } : x));
      setDirty((d) => { const c = { ...d }; delete c[userId]; return c; });
    } catch (e) {
      toast.error(e.response?.data?.detail || `Αποτυχία: ${u.name}`);
    }
  };

  const saveAll = async () => {
    setBusy(true);
    let ok = 0, fail = 0;
    for (const userId of Object.keys(dirty)) {
      const u = users.find((x) => x.id === userId);
      const merged = { ...(u?.permissions || {}), ...dirty[userId] };
      try {
        await http.put(`/users/${userId}`, { permissions: merged });
        setUsers((arr) => arr.map((x) => x.id === userId ? { ...x, permissions: merged } : x));
        ok++;
      } catch { fail++; }
    }
    setDirty({});
    setBusy(false);
    if (fail === 0) toast.success(`Αποθηκεύτηκαν ${ok} χρήστες`);
    else toast.error(`${ok} επιτυχείς, ${fail} αποτυχίες`);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (q && !u.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, query, roleFilter]);

  // Group permissions by section so the matrix header is readable.
  const sections = useMemo(() => {
    const groups = { module: [], reports: [], view: [], order: [] };
    for (const f of (catalog.fields || [])) {
      if (f.key.startsWith("module_")) groups.module.push(f);
      else if (f.key.startsWith("reports_")) groups.reports.push(f);
      else if (f.key.startsWith("view_") || f.key === "can_print") groups.view.push(f);
      else groups.order.push(f);
    }
    return groups;
  }, [catalog]);

  const dirtyCount = Object.keys(dirty).length;

  return (
    <Card className="p-4 sm:p-6 space-y-4" data-testid="permissions-matrix-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Shield size={18} /> Δικαιώματα Χειριστών
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Matrix view: επεξεργαστείτε όλα τα permissions σε ένα grid. Οι αλλαγές αποθηκεύονται όταν πατήσετε «Αποθήκευση».
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirtyCount > 0 && (
            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-bold border border-amber-300">
              {dirtyCount} εκκρεμείς
            </span>
          )}
          <Button
            onClick={saveAll}
            disabled={busy || dirtyCount === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="permissions-save-all"
          >
            <Save size={14} className="mr-1" /> Αποθήκευση όλων
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Αναζήτηση χειριστή…"
            className="h-9 w-full pl-8 pr-2 border border-slate-300 rounded-md text-sm"
            data-testid="permissions-search"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-9 px-2 border border-slate-300 rounded-md text-sm"
          data-testid="permissions-role-filter"
        >
          <option value="all">Όλοι οι ρόλοι</option>
          {(catalog.roles || []).map((r) => (
            <option key={r.code} value={r.code}>{r.label}</option>
          ))}
        </select>
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[70vh]" data-testid="permissions-matrix">
        <table className="text-xs border-collapse">
          <thead className="sticky top-0 z-10 bg-slate-50">
            {/* Section group row */}
            <tr>
              <th className="sticky left-0 z-20 bg-slate-50 border-r border-b border-slate-300 p-2 text-left min-w-[200px]">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">Χειριστής</span>
              </th>
              {[
                { label: "Modules", cells: sections.module, tone: "blue" },
                { label: "Αναφορές", cells: sections.reports, tone: "emerald" },
                { label: "Όψεις", cells: sections.view, tone: "purple" },
                { label: "Παραγγελίες", cells: sections.order, tone: "amber" },
              ].map((g) => (
                <th
                  key={g.label}
                  colSpan={g.cells.length}
                  className={`border-b border-slate-300 p-1 text-center text-[10px] uppercase font-bold tracking-wider bg-${g.tone}-50 text-${g.tone}-800`}
                >
                  {g.label}
                </th>
              ))}
              <th className="sticky right-0 z-20 bg-slate-50 border-l border-b border-slate-300 p-2 w-[140px]">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">Ενέργειες</span>
              </th>
            </tr>
            {/* Per-permission column header */}
            <tr>
              <th className="sticky left-0 z-20 bg-slate-50 border-r border-b border-slate-300"></th>
              {[...sections.module, ...sections.reports, ...sections.view, ...sections.order].map((f) => (
                <th
                  key={f.key}
                  className="border-b border-slate-200 p-1 text-[10px] font-medium whitespace-nowrap"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", height: 130, padding: "8px 4px" }}
                  title={f.label}
                  data-testid={`col-header-${f.key}`}
                >
                  {f.label}
                </th>
              ))}
              <th className="sticky right-0 z-20 bg-slate-50 border-l border-b border-slate-300"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const userDirty = dirty[u.id] || {};
              const isDirty = Object.keys(userDirty).length > 0;
              const effective = (key) =>
                key in userDirty ? userDirty[key] : !!(u.permissions || {})[key];
              const canEdit = isSuper || (u.role !== "super_manager" && u.role !== "manager");
              return (
                <tr
                  key={u.id}
                  className={`border-b border-slate-100 hover:bg-slate-50/50 ${isDirty ? "bg-amber-50/40" : ""}`}
                  data-testid={`matrix-row-${u.id}`}
                >
                  <td className="sticky left-0 z-[5] border-r border-slate-200 p-2 bg-white" style={isDirty ? { background: "#fffbeb" } : {}}>
                    <div className="font-medium text-slate-800 truncate max-w-[180px]" title={u.name}>{u.name}</div>
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-bold border ${ROLE_BADGE[u.role] || ""}`}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  {[...sections.module, ...sections.reports, ...sections.view, ...sections.order].map((f) => {
                    const val = effective(f.key);
                    const isChanged = f.key in userDirty;
                    return (
                      <td
                        key={f.key}
                        className={`p-1 text-center align-middle border-r border-slate-100 ${isChanged ? "bg-amber-100/60" : ""}`}
                      >
                        <Switch
                          checked={val}
                          onCheckedChange={() => togglePerm(u.id, f.key, val)}
                          disabled={!canEdit}
                          data-testid={`matrix-cell-${u.id}-${f.key}`}
                        />
                      </td>
                    );
                  })}
                  <td className="sticky right-0 z-[5] border-l border-slate-200 p-1 bg-white" style={isDirty ? { background: "#fffbeb" } : {}}>
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => resetRowToTemplate(u.id)}
                        disabled={!canEdit}
                        className="h-6 text-[10px] px-1.5"
                        title="Επαναφορά στο default του ρόλου"
                        data-testid={`matrix-reset-${u.id}`}
                      >
                        <RotateCcw size={10} className="mr-0.5" /> Reset
                      </Button>
                      {isDirty && (
                        <Button
                          size="sm"
                          onClick={() => saveOne(u.id)}
                          className="h-6 text-[10px] px-1.5 bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`matrix-save-${u.id}`}
                        >
                          <Save size={10} className="mr-0.5" /> Αποθ.
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={catalog.fields.length + 2} className="text-center py-10 text-slate-400 italic">
                  Δεν βρέθηκαν χειριστές
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="text-[11px] text-slate-500 flex flex-wrap gap-3">
        <span><b>Reset</b> = επαναφορά στο default του ρόλου</span>
        <span><b>🟧 Amber row</b> = εκκρεμείς αλλαγές</span>
        {!isSuper && <span className="text-amber-700">Μόνο ο Σούπερ Manager επεξεργάζεται άλλους managers.</span>}
      </div>
    </Card>
  );
}
